import React, { useEffect, useRef, useState } from 'react';
import logger from '@/logger';
import { connect } from 'react-redux';
import { useLocation } from 'react-router-dom';
import Loader from 'components/Loader';
import FirstEventWalkthrough from 'components/FirstEventWalkthrough';
import { ClientSubscriptions } from 'components/WebsocketSubscriptions';
import EventsList from './EventsList';
import { thunkFetchEventsIncDel, thunkFetchClient, setCurrentClientID } from '@/redux/actions';
import { isSaaSAdmin, isSuperAdmin as isSuper, hasSomeRoleInEvent } from '@/redux/util';

import './EventsListLoader.scss';

const EventsListContainer = props => {
	const {
		currentClient,
		setCurrentClientID,
		fetchEventsIncDel,
		events,
		admins,
		adminSSOProfile,
	} = props;
	const location = useLocation();
	const { from } = location.state || {};
	const isSaaS = isSaaSAdmin();
	const isSuperAdmin = isSuper();
	const { email } = adminSSOProfile;
	const adminPermissions = admins[email];
	const hasComputedEvents = useRef(false);

	const [showLoader, setShowLoader] = useState('loader-hide');
	const [adminsEvents, setAdminsEvents] = useState([]);
	const [unpaidEvents, setUnpaidEvents] = useState([]);

	useEffect(() => {
		const from = 'EventsListContainer useEffect';
		if (currentClient.id) {
			fetchEventsIncDel(currentClient.id, from);
		}
	}, [currentClient.id]);

	useEffect(() => {
		const newAdminsEvents = Object.values(events)
			.filter(event => hasSomeRoleInEvent(event) && !event.deleted_at)
			.filter(event => event.client_id === currentClient.id);
		setAdminsEvents(newAdminsEvents);
	}, [admins, events, currentClient.id]);

	useEffect(() => {
		hasComputedEvents.current = true;
	}, [adminsEvents]);

	useEffect(() => {
		const ownedEvents = adminsEvents.filter(event => event.owner_id === adminPermissions.id);
		const newUnpaidEvents = ownedEvents.filter(event => !event.payments || !event.payments.length);
		setUnpaidEvents(newUnpaidEvents);
	}, [adminsEvents]);

	useEffect(() => {
		const loaderTimer = setTimeout(() => {
			setShowLoader('loader-show');
		}, 5000);

		return () => {
			clearTimeout(loaderTimer);
		};
	}, []);

	const eventListLoader = (
		<div className="event-list-loader">
			<div className={showLoader}>
				<Loader />
			</div>
		</div>
	);

	if (!currentClient || !currentClient.id) return eventListLoader;
	if (!isSuperAdmin && (!adminPermissions || !adminPermissions.id)) return eventListLoader;
	if (!hasComputedEvents.current) return eventListLoader;

	const permsOnCurrentClient =
		adminPermissions && adminPermissions.clients.some(client => client.id === currentClient.id);
	const eventPermsOnCurrentClient =
		adminPermissions && adminPermissions.events.some(event => event.client_id === currentClient.id);

	const canCreateEvent = isSuperAdmin || permsOnCurrentClient || isSaaS;

	if (
		adminsEvents.length === 0 &&
		!isSuperAdmin &&
		!permsOnCurrentClient &&
		!eventPermsOnCurrentClient &&
		adminPermissions
	) {
		if (adminPermissions.clients && adminPermissions.clients.length > 0) {
			logger.warn('No perms on this client - sending to first client.', {
				location: 'EventsListContainer.useEffect',
			});
			const firstClient = adminPermissions.clients[0];
			setCurrentClientID(firstClient.id);
		} else if (adminPermissions.events && adminPermissions.events.length > 0) {
			logger.warn("No perms on this event - sending to first event's client.", {
				location: 'EventsListContainer.useEffect',
			});
			const firstEvent = adminPermissions.events[0];
			setCurrentClientID(firstEvent.client_id);
		}
	}

	const screen =
		isSaaS && adminsEvents.length === 0 && currentClient.id === 1 ? (
			<FirstEventWalkthrough client={currentClient} />
		) : (
			<EventsList
				events={adminsEvents}
				numUnpaidEvents={isSaaS ? unpaidEvents.length : 0}
				client={currentClient}
				canCreateEvent={canCreateEvent}
				curtain={from === 'Login'}
			/>
		);

	return <ClientSubscriptions route="/events/">{screen}</ClientSubscriptions>;
};

const msp = ({ clients, auth, events, admins }) => ({
	adminSSOProfile: auth.adminSSOProfile,
	admins,
	currentClient: clients.currentClient,
	events: events.events,
});

const mdp = dispatch => ({
	fetchEventsIncDel: (clientID, from) => dispatch(thunkFetchEventsIncDel(clientID, from)),
	fetchClient: (clientID, from) => dispatch(thunkFetchClient(clientID, from)),
	setCurrentClientID: clientID => dispatch(setCurrentClientID(clientID)),
});

export default connect(msp, mdp)(EventsListContainer);
